import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { pb, getCurrentUser } from "../services/pocketbase";

const AdminClipUploader = ({ open, onClose, editClip = null }) => {
  const [error, setError] = useState(null);
  const user = getCurrentUser();
  const [formData, setFormData] = useState({
    game: "",
    map: "",
    clip_title: "",
    clip_link: "",
    discord_username: "",
    highest_round: "",
    paypal_handle: "",
  });

  // Use useEffect to update form data when editClip changes
  useEffect(() => {
    if (editClip) {
      setFormData({
        game: editClip.game || "",
        map: editClip.map || "",
        clip_title: editClip.clip_title || "",
        clip_link: editClip.clip_link || "",
        discord_username: editClip.discord_username || "",
        highest_round: editClip.highest_round || "",
        paypal_handle: editClip.paypal_handle || "",
      });
    } else {
      // Reset form when creating a new clip
      setFormData({
        game: "",
        map: "",
        clip_title: "",
        clip_link: "",
        discord_username: "",
        highest_round: "",
        paypal_handle: "",
      });
    }
  }, [editClip]);

  const games = [
    { value: "blackops6", label: "Black Ops 6" },
    { value: "gta5", label: "GTA 5" },
    { value: "other", label: "Other" },
  ];

  const maps = [
    { value: "liberty_falls", label: "Liberty Falls" },
    { value: "terminus", label: "Terminus" },
    { value: "cittadelle_de_morts", label: "Cittadelle de Morts" },
    { value: "the_toumb", label: "The Toumb" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Function to send webhook notification
  const sendWebhookNotification = async (clipData, isNew = false, originalClip = null) => {
    try {
      const currentUser = user;
      const timestamp = new Date().toISOString();
      
      let fields = [];
      let description = "";
      
      if (isNew) {
        // New clip created
        description = `**New clip created by ${currentUser.username}**`;
        fields = [
          {
            name: "Clip Title",
            value: clipData.clip_title,
            inline: true,
          },
          {
            name: "Game",
            value: getGameDisplayName(clipData.game),
            inline: true,
          }
        ];
        
        if (clipData.game === "blackops6") {
          fields.push(
            {
              name: "Map",
              value: getMapDisplayName(clipData.map),
              inline: true,
            },
            {
              name: "Highest Round",
              value: clipData.highest_round.toString(),
              inline: true,
            }
          );
        }
        
        fields.push(
          {
            name: "Clip Link",
            value: clipData.clip_link,
          },
          {
            name: "Created At",
            value: new Date().toLocaleString(),
          }
        );
      } else {
        // Clip edited - find changes
        description = `**Clip edited by ${currentUser.username}**`;
        
        // Compare old and new values
        const changes = [];
        if (originalClip.clip_title !== clipData.clip_title) {
          changes.push({
            name: "Title",
            value: `Changed from "${originalClip.clip_title}" to "${clipData.clip_title}"`,
          });
        }
        
        if (originalClip.game !== clipData.game) {
          changes.push({
            name: "Game",
            value: `Changed from "${getGameDisplayName(originalClip.game)}" to "${getGameDisplayName(clipData.game)}"`,
          });
        }
        
        if (originalClip.map !== clipData.map) {
          changes.push({
            name: "Map",
            value: `Changed from "${getMapDisplayName(originalClip.map)}" to "${getMapDisplayName(clipData.map)}"`,
          });
        }
        
        if (originalClip.highest_round !== clipData.highest_round) {
          changes.push({
            name: "Highest Round",
            value: `Changed from "${originalClip.highest_round}" to "${clipData.highest_round}"`,
          });
        }
        
        if (originalClip.clip_link !== clipData.clip_link) {
          changes.push({
            name: "Clip Link",
            value: `Link was updated`,
          });
        }
        
        // Add clip title as first field
        fields = [
          {
            name: "Clip Title",
            value: clipData.clip_title,
            inline: false,
          },
          ...changes.map((change) => ({
            name: change.name,
            value: change.value,
            inline: false,
          })),
        ];
        
        fields.push({
          name: "Edited At",
          value: new Date().toLocaleString(),
        });
        
        // If no changes detected
        if (changes.length === 0) {
          fields.push({
            name: "Note",
            value: "No significant changes detected",
          });
        }
      }
      
      // Prepare webhook data
      const webhookData = {
        embeds: [
          {
            title: isNew ? "New Clip Created" : "Clip Edited",
            description: description,
            color: isNew ? 0x00ff00 : 0xffaa00,
            fields: fields,
            timestamp: timestamp,
            footer: {
              text: `Admin: ${currentUser.username}`,
            },
          },
        ],
      };
      
      // Send to webhook endpoint
      const response = await fetch("/api/webhooks/clip-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });
      
      if (!response.ok) {
        console.error("Failed to send webhook notification");
      }
    } catch (err) {
      console.error("Error sending webhook notification:", err);
    }
  };
  
  // Helper functions for display names
  const getGameDisplayName = (gameId) => {
    const game = games.find(g => g.value === gameId);
    return game ? game.label : gameId;
  };
  
  const getMapDisplayName = (mapId) => {
    const map = maps.find(m => m.value === mapId);
    return map ? map.label : mapId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // Find user by discord_username
      let targetUserId = null;

      if (formData.discord_username) {
        try {
          // First try to find by username field
          const userRecords = await pb.collection("users").getList(1, 1, {
            filter: `username="${formData.discord_username}"`,
          });

          if (userRecords.items.length > 0) {
            targetUserId = userRecords.items[0].id;
          } else {
            // If not found, try searching by discord_username field if it exists
            const discordUserRecords = await pb
              .collection("users")
              .getList(1, 1, {
                filter: `discord_username="${formData.discord_username}"`,
              });

            if (discordUserRecords.items.length > 0) {
              targetUserId = discordUserRecords.items[0].id;
            }
          }
        } catch (err) {
          console.error("Error finding user:", err);
        }
      }

      const clipData = {
        user: targetUserId || user.id, // Use found user ID or fallback to current admin
        game: formData.game,
        map: formData.map,
        clip_title: formData.clip_title,
        clip_link: formData.clip_link,
        discord_username: formData.discord_username,
        highest_round: formData.highest_round
          ? parseInt(formData.highest_round, 10)
          : 0,
        paypal_handle: formData.paypal_handle,
      };

      if (editClip) {
        // Update existing clip
        await pb.collection("clips").update(editClip.id, clipData);
        // Send webhook for edit
        await sendWebhookNotification(clipData, false, editClip);
      } else {
        // Create new clip
        await pb.collection("clips").create(clipData);
        // Send webhook for new clip
        await sendWebhookNotification(clipData, true);
      }

      // Reset form and close dialog
      setFormData({
        game: "",
        map: "",
        clip_title: "",
        clip_link: "",
        discord_username: "",
        highest_round: "",
        paypal_handle: "",
      });
      onClose(true);
    } catch (err) {
      console.error("Error uploading clip:", err);
      setError(err.message || "Failed to upload clip");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      sx={{ zIndex: 99 }}
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {editClip ? "Edit Clip" : "Admin Clip Upload"}
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Game</InputLabel>
              <Select
                name="game"
                value={formData.game}
                onChange={handleInputChange}
                required
              >
                {games.map((game) => (
                  <MenuItem key={game.value} value={game.value}>
                    {game.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.game === "blackops6" && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Map</InputLabel>
                  <Select
                    name="map"
                    value={formData.map}
                    onChange={handleInputChange}
                    required
                  >
                    {maps.map((map) => (
                      <MenuItem key={map.value} value={map.value}>
                        {map.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Highest Round"
                  name="highest_round"
                  type="number"
                  value={formData.highest_round}
                  onChange={handleInputChange}
                />
              </>
            )}

            <TextField
              fullWidth
              label="Clip Title"
              name="clip_title"
              value={formData.clip_title}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              label="Clip Link (YouTube, Twitch, etc.)"
              name="clip_link"
              value={formData.clip_link}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              label="Discord Username"
              name="discord_username"
              value={formData.discord_username}
              onChange={handleInputChange}
              helperText="Leave blank to assign to yourself"
            />

            {formData.game === "blackops6" && (
              <TextField
                fullWidth
                label="PayPal Handle"
                name="paypal_handle"
                value={formData.paypal_handle}
                onChange={handleInputChange}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="submit" variant="contained">
            {editClip ? "Update Clip" : "Upload Clip"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AdminClipUploader;
